import type {
  CollectionReference,
  PartialWithFieldValue,
  Query,
} from 'firebase/firestore'
import {
  and,
  collection,
  collectionGroup,
  doc,
  query,
  serverTimestamp,
  where,
  type Firestore,
  type FirestoreDataConverter,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { FirebaseRepository } from '../../models/FirebaseRepository'
import { modelListStream } from '../../firestore-mobx/stream'
import { modelItemStream } from '../../firestore-mobx/stream'
import { StaticModelCollection } from '../../firestore-mobx/model'
import { setDocWithError } from '../../firestore-mobx/fetch'
import type { FirestoreRoomStateUPRPeerReview } from './schema'
import { empty, schema, writeSchema } from './schema'
import { RoomStateUPRPeerReview } from '../../models/RoomStateUPRPeerReview'

export * from './schema'

const converter: FirestoreDataConverter<FirestoreRoomStateUPRPeerReview> = {
  toFirestore: (
    data: PartialWithFieldValue<FirestoreRoomStateUPRPeerReview>
  ) => {
    writeSchema.partial().parse(data)
    return data
  },
  fromFirestore: (snapshot: QueryDocumentSnapshot) => {
    const data = snapshot.data({ serverTimestamps: 'estimate' })
    return schema.parse(data)
  },
}

export const getColGroupRef = (
  firestore: Firestore
): Query<FirestoreRoomStateUPRPeerReview> => {
  return collectionGroup(firestore, 'peer_review').withConverter(converter)
}

export const getColRef = (
  firestore: Firestore,
  roomId: string,
  userId: string
): CollectionReference<FirestoreRoomStateUPRPeerReview> => {
  return collection(
    firestore,
    'room_state',
    roomId,
    'user_peer_review',
    userId,
    'peer_review'
  ).withConverter(converter)
}

export const buildEmptyRoomStateUPRPeerReviewCollection = (
  repository: FirebaseRepository
) => {
  return new StaticModelCollection({
    repository,
    model: RoomStateUPRPeerReview,
    empty,
  })
}

export const setPeerReview = async (
  repository: FirebaseRepository,
  params: {
    roomId: string
    sectionId: string
    assignmentId: string
    userId: string
    reviewerUserId: string
    review: Record<string, number>
  }
) => {
  const { firestore } = repository
  const { roomId, sectionId, assignmentId, userId, reviewerUserId, review } =
    params
  const colRef = getColRef(firestore, roomId, userId)
  const docRef = doc(colRef, reviewerUserId)
  const payload: FirestoreRoomStateUPRPeerReview = {
    roomId,
    sectionId,
    assignmentId,
    userId,
    reviewerUserId,
    review,
    updatedAt: serverTimestamp() as unknown as Date,
  }
  await setDocWithError(docRef, payload, {
    merge: true,
    errorName: 'SetPeerReviewError',
  })
  return docRef
}

export const getPeerReviewsForReviewerInRoom = (
  repository: FirebaseRepository,
  { roomId, reviewerUserId }: { roomId: string; reviewerUserId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('roomId', '==', roomId),
      where('reviewerUserId', '==', reviewerUserId)
    )
  )
  return modelListStream(repository, q, RoomStateUPRPeerReview)
}

export const getPeerReviewsForAssignment = (
  repository: FirebaseRepository,
  { sectionId, assignmentId }: { sectionId: string; assignmentId: string }
) => {
  const q = query(
    getColGroupRef(repository.firestore),
    and(
      where('sectionId', '==', sectionId),
      where('assignmentId', '==', assignmentId)
    )
  )
  return modelListStream(repository, q, RoomStateUPRPeerReview)
}

export const getPeerReviewsForRevieweeInRoom = (
  repository: FirebaseRepository,
  { roomId, revieweeUserId }: { roomId: string; revieweeUserId: string }
) => {
  const colRef = getColRef(repository.firestore, roomId, revieweeUserId)
  return modelListStream(repository, colRef, RoomStateUPRPeerReview)
}

export const getPeerReviewForRevieweeByReviewer = (
  repository: FirebaseRepository,
  {
    roomId,
    revieweeUserId,
    reviewerUserId,
  }: {
    roomId: string
    revieweeUserId: string
    reviewerUserId: string
  }
) => {
  const colRef = getColRef(repository.firestore, roomId, revieweeUserId)
  const docRef = doc(colRef, reviewerUserId)
  return modelItemStream(repository, docRef, RoomStateUPRPeerReview)
}
